import React, { useMemo } from 'react';
import { TableModal } from './Modal';

// OEM Distribution data type
export interface OEMDistributionData {
    oemName: string;
    totalVehicles: number;  
    percentageTotal: number;
    activeVehicles: number;  
    idleVehicles: number;   
    inRepairVehicles: number;
    brokenVehicles: number;  
    lostVehicles: number;     
}

interface OEMDistributionModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: OEMDistributionData[];
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

// Sort field enum for type safety
enum OEMSortField {
    OEM_NAME = 'oemName',
    TOTAL_VEHICLE = 'totalVehicles',    
    PERCENTAGE_TOTAL = 'percentageTotal',
    ACTIVE = 'activeVehicles',            
    IDLE = 'idleVehicles',                
    IN_REPAIR = 'inRepairVehicles',        
    BROKEN = 'brokenVehicles',            
    LOST = 'lostVehicles'                  
}

const OEMDistributionModal: React.FC<OEMDistributionModalProps> = ({
    isOpen,
    onClose,
    data,
    loading = false,
    hasMore = false,
    onLoadMore
}) => {

    // Column configuration
    const columns = useMemo(() => [
        {
            key: OEMSortField.OEM_NAME,
            title: 'OEM Name',
            sortable: true,
            render: (value: string) => (
                <span 
                    className="font-jakarta"
                    style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.006em',
                        color: '#171717'
                    }}
                >
                    {value}
                </span>
            )
        },
        {
            key: OEMSortField.TOTAL_VEHICLE,
            title: 'Total Vehicle',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="font-jakarta"
                    style={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.006em',
                        color: '#171717'
                    }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: OEMSortField.PERCENTAGE_TOTAL,
            title: '% of Total Vehicle',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {parseFloat(value.toFixed(2))}%
                </span>
            )
        },
        {
            key: OEMSortField.ACTIVE,
            title: 'Active',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="font-jakarta"
                    style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '-0.006em',
                        color: '#171717'
                    }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: OEMSortField.IDLE,
            title: 'Idle',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: OEMSortField.IN_REPAIR,
            title: 'In Repair',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: OEMSortField.BROKEN,
            title: 'Broken',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        },
        {
            key: OEMSortField.LOST,
            title: 'Lost',
            sortable: true,
            render: (value: number) => (
                <span 
                    className="text-[14px]"
                    style={{ color: '#5C5C5C' }}
                >
                    {value.toLocaleString()}
                </span>
            )
        }
    ], []);

    // Column order
    const getColumnOrder = () => [
        OEMSortField.OEM_NAME,
        OEMSortField.TOTAL_VEHICLE,
        OEMSortField.PERCENTAGE_TOTAL,
        OEMSortField.ACTIVE,
        OEMSortField.IDLE,
        OEMSortField.IN_REPAIR,
        OEMSortField.BROKEN,
        OEMSortField.LOST
    ];

    // Sort function
    const handleSort = (data: OEMDistributionData[], field: string, direction: 'asc' | 'desc') => {
        return [...data].sort((a, b) => {
            const aValue = a[field as keyof OEMDistributionData];
            const bValue = b[field as keyof OEMDistributionData];

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    };

    // CSV value getter
    const getCsvValue = (row: OEMDistributionData, field: string) => {
        const value = row[field as keyof OEMDistributionData];
        
        if (value === undefined || value === null) {
            return '';
        }
        
        if (typeof value === 'string') {
            return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
        }
        
        if (typeof value === 'number') {
            if (field === 'percentageTotal') {
                return `${value}%`;
            }
            return value.toString();
        }
        
        return String(value);
    };

    return (
        <TableModal
            isOpen={isOpen}
            onClose={onClose}
            title="OEM Distribution Detail"
            modalTitle="OEM Distribution"
            description="View detailed vehicle status counts for each manufacturer."
            data={data}
            columns={columns}
            sortConfig={{
                defaultField: OEMSortField.TOTAL_VEHICLE,
                defaultDirection: 'desc'
            }}
            csvFileName="oem-distribution.csv"
            onSort={handleSort}
            getColumnOrder={getColumnOrder}
            getCsvValue={getCsvValue}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
        />
    );
};

export default OEMDistributionModal;